import {
  AppCard,
  FormAsyncAutocomplete,
  FormStaticAutocomplete,
  FormTextField,
  Icon,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiContentSaveOutline, mdiExclamation } from "@mdi/js";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { Material } from "../../../../../@types/services/Material";
import type { PlantField } from "../../../../../@types/services/PlantField";
import type { RequesterArea } from "../../../../../@types/services/RequesterArea";
import { listPlants } from "../../../../../__generated__/orval/endpoints/plants/plants";
import type { EntitiesPlantListResponse } from "../../../../../__generated__/orval/models/entitiesPlantListResponse.zod";
import type { ListPlantsParams } from "../../../../../__generated__/orval/models/listPlantsParams.zod";
import { MaterialNumberField } from "../../../../../components/molecules/MaterialNumberField";
import { qualityDeviationOptions } from "../../../../../constants/mmRequest/qualityDeviationOptions";
import { ForbiddenError } from "../../../../../errors/ForbiddenError";
import { fetchPlantFields } from "../../../../../services/plantFields";
import { fetchRequesterAreas } from "../../../../../services/requesterAreas";
import {
  fetchValorationRequest,
  updateValorationRequest,
} from "../../../../../services/valorationRequests";

type FormValues = {
  mm_request_attributes: {
    id: number;
    matnumber: string[];
    subject: string;
    description: string;
    requester_field: PlantField;
    requester_area: RequesterArea;
    quality_deviation: string[];
    quality_deviation_description: string;
  };
  plant_ids: EntitiesPlantListResponse["results"];
};

const EditValorationRequest: React.FC = () => {
  const { t } = useTranslation();

  const { valorationRequestId } = Route.useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [formData, setFormData] = useState<FormValues | null>(null);
  const [mmRequestId, setMmRequestId] = useState<number | null>(null);
  const { handleSubmit, control, reset, setValue, watch } =
    useForm<FormValues>();

  const requesterField = watch("mm_request_attributes.requester_field");
  const title = mmRequestId
    ? `${t("common.valorationRequest")} #${mmRequestId}`
    : t("common.valorationRequest");

  const handleClose = useCallback(() => {
    setOpenConfirmDialog(false);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!formData) return;

    handleClose();
    setLoadingSubmit(true);

    const formattedData = {
      mm_request_attributes: {
        description: formData.mm_request_attributes.description,
        id: formData.mm_request_attributes.id,
        matnumber: formData.mm_request_attributes.matnumber.map((material) =>
          typeof material === "string"
            ? material
            : (material as Material).matnumber,
        ),
        quality_deviation: formData.mm_request_attributes.quality_deviation,
        quality_deviation_description:
          formData.mm_request_attributes.quality_deviation_description,
        requester_area: formData.mm_request_attributes.requester_area.name,
        requester_field: formData.mm_request_attributes.requester_field.name,
        subject: formData.mm_request_attributes.subject,
      },
      plant_ids: formData.plant_ids.map((plant) => plant.id.toString()),
    };

    await updateValorationRequest(valorationRequestId, formattedData)
      .then(() => {
        enqueueSnackbar(t("valorationRequests.requestUpdated"), {
          variant: "success",
        });
        navigate({
          params: { valorationRequestId },
          to: "/vbuyer/valoration_requests/$valorationRequestId",
        });
      })
      .catch(() => {
        setLoadingSubmit(false);
        enqueueSnackbar(t("valorationRequests.errorMessage"), {
          variant: "error",
        });
      });
  }, [formData, handleClose, valorationRequestId, navigate, t]);

  const onSubmit: SubmitHandler<FormValues> = useCallback(async (data) => {
    setFormData(data);
    setOpenConfirmDialog(true);
  }, []);

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      try {
        if (valorationRequestId) {
          const valorationRequest =
            await fetchValorationRequest(valorationRequestId);
          setMmRequestId(valorationRequest.mm_request.id);

          let plantsFormatted: EntitiesPlantListResponse["results"] = [];
          if (valorationRequest.plant_ids?.length > 0) {
            const plantsData = await listPlants({
              id: valorationRequest.plant_ids,
            });
            plantsFormatted = plantsData.results;
          }
          reset({
            mm_request_attributes: {
              description: valorationRequest.mm_request.description || "",
              id: valorationRequest.mm_request.id,
              matnumber: valorationRequest.mm_request.matnumber || [],
              quality_deviation:
                valorationRequest.mm_request.quality_deviation || [],
              quality_deviation_description:
                valorationRequest.mm_request.quality_deviation_description ||
                "",
              requester_area: valorationRequest.mm_request.requester_area || "",
              requester_field:
                valorationRequest.mm_request.requester_field || "",
              subject: valorationRequest.mm_request.subject || "",
            },
            plant_ids: plantsFormatted,
          });
        }
      } catch {
        enqueueSnackbar(t("valorationRequests.errorMessage"), {
          variant: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [valorationRequestId, reset, t]);

  if (loading) {
    return (
      <PageContainer
        backTo={`/vbuyer/valoration_requests/${valorationRequestId}`}
        maxWidth="md"
        showBackButton
        title={title}
      >
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  return (
    <>
      <PageContainer
        backTo={`/vbuyer/valoration_requests/${valorationRequestId}`}
        maxWidth="md"
        showBackButton
        title={title}
      >
        <AppCard>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                width: "100%",
              }}
            >
              <MaterialNumberField
                control={control}
                helperText={t("valorationRequests.materialHelper")}
                label={t("valorationRequests.material")}
                name="mm_request_attributes.matnumber"
                rules={{ required: t("valorationRequests.requiredMessage") }}
                setValue={setValue}
              />

              <FormAsyncAutocomplete
                control={control}
                fetchOptions={fetchPlantFields}
                filters={(searchTerm) => ({
                  active_on_mm_request: true,
                  enabled: true,
                  name: searchTerm,
                })}
                getOptionLabel={(option) => option.name || ""}
                isOptionEqualToValue={(option, value) =>
                  typeof value !== "string" && option.id === value.id
                }
                label={t("valorationRequests.requesterField")}
                name="mm_request_attributes.requester_field"
                queryKey={["requesterFields", "infinite"]}
                required
                rules={{
                  required: t(
                    "valorationRequests.requesterFieldRequiredMessage",
                  ),
                }}
              />

              <FormAsyncAutocomplete
                control={control}
                disabled={!requesterField}
                fetchOptions={fetchRequesterAreas}
                filters={(searchTerm) => ({
                  enabled: true,
                  name: searchTerm,
                  plant_field_name: requesterField ? requesterField.name : "",
                })}
                getOptionLabel={(option) => option.name || ""}
                isOptionEqualToValue={(option, value) =>
                  typeof value !== "string" && option.id === value.id
                }
                label={t("valorationRequests.requesterArea")}
                name="mm_request_attributes.requester_area"
                queryKey={["requesterAreas", "infinite"]}
              />

              <FormAsyncAutocomplete
                control={control}
                fetchOptions={listPlants}
                filters={(searchTerm): ListPlantsParams => ({
                  active_on_mm_request: true,
                  enabled: true,
                  plant_number_with_name: searchTerm,
                })}
                getOptionLabel={(option) => option.plant_number_with_name}
                label={t("valorationRequests.plants")}
                minSearchLength={2}
                multiple
                name="plant_ids"
                queryKey={["plants", "infinite"]}
              />

              <FormTextField
                control={control}
                helperText={t("mmRequests.subjectHelper")}
                label={t("valorationRequests.subject")}
                name="mm_request_attributes.subject"
              />

              <FormTextField
                control={control}
                helperText={t("mmRequests.descriptionHelper")}
                label={t("valorationRequests.description")}
                multiline
                name="mm_request_attributes.description"
                rows={6}
              />

              <FormStaticAutocomplete
                control={control}
                getOptionLabel={(option) =>
                  option ? t(`mmRequests.qualityDeviation.${option}`) : ""
                }
                label={t("valorationRequests.qualityDeviation")}
                multiple
                name="mm_request_attributes.quality_deviation"
                options={qualityDeviationOptions}
              />

              <FormTextField
                control={control}
                label={t("valorationRequests.qualityDeviationDescription")}
                multiline
                name="mm_request_attributes.quality_deviation_description"
                rows={3}
              />

              <Button
                color="success"
                disabled={loadingSubmit}
                startIcon={
                  loadingSubmit ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : (
                    <Icon icon={mdiContentSaveOutline} />
                  )
                }
                sx={{
                  alignSelf: "flex-end",
                  maxWidth: "100%",
                  minWidth: 0,
                  width: "fit-content",
                }}
                type="submit"
                variant="contained"
              >
                {t("common.save")}
              </Button>
            </Box>
          </form>
        </AppCard>
      </PageContainer>
      <Dialog
        aria-describedby="alert-dialog-description"
        aria-labelledby="alert-dialog-title"
        onClose={handleClose}
        open={openConfirmDialog}
        slotProps={{
          paper: {
            sx: {
              pt: 4,
              px: 2,
              textAlign: "center",
              width: "600px",
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 3,
            mt: -1,
          }}
        >
          <Box
            sx={{
              alignItems: "center",
              backgroundColor: "white",
              border: "3px solid",
              borderColor: "#ED6C02",
              borderRadius: "50%",
              color: "#ED6C02",
              display: "flex",
              height: "80px",
              justifyContent: "center",
              width: "80px",
            }}
          >
            <Icon fontSize="large" icon={mdiExclamation} />
          </Box>
        </Box>

        <DialogTitle sx={{ textAlign: "center" }}>
          {t("valorationRequests.modalTitle")}
        </DialogTitle>

        <DialogContent>
          <DialogContentText
            sx={{ textAlign: "center", whiteSpace: "pre-line" }}
          >
            {t("valorationRequests.modalMessage")}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>{t("cancel")}</Button>
          <Button autoFocus onClick={handleConfirm}>
            {t("ok")}
          </Button>
        </DialogActions>
      </Dialog>
      {!!loadingSubmit && (
        <Dialog open>
          <DialogContent sx={{ p: 4, textAlign: "center" }}>
            <CircularProgress />
            <DialogContentText sx={{ mt: 2 }}>
              {t("valorationRequests.dialog.processing")}
            </DialogContentText>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/valoration_requests/$valorationRequestId/edit",
)({
  beforeLoad({ context }) {
    if (context.auth.user?.roles_names.includes("Edit material requests")) {
      return;
    }

    throw new ForbiddenError();
  },
  component: EditValorationRequest,
});
